import { useEffect, useId, useRef, useState } from 'react';
import {
  List, BarChart3, Trophy, TrendingUp, Activity, Search, Radar, Wrench,
  ChevronDown, User, Power, ShieldCheck, Download,
  Eye, Film, Bug, MessageSquareWarning, FileText,
} from 'lucide-react';
import HelpButton from '../HelpButton/HelpButton';
import DesktopDownloadDialog from './DesktopDownloadDialog';
import styles from './AppNav.module.css';
import BetaBadge from './BetaBadge';

const MENU_ITEMS = [
  {
    key: 'sessions', label: 'Sessions', Icon: List, prop: 'onGoToSessions',
    description: 'Upload and manage your runs',
  },
  {key: 'studio', label: 'Video Studio', beta: true, Icon: Film, prop: 'onGoToStudio', description: 'Compose synchronized video clips'},
  {
    key: 'leaderboard', label: 'Leaderboard', Icon: Trophy, prop: 'onGoToLeaderboard',
    description: 'Compare lap times and drivers',
  },
  {
    key: 'progress', label: 'Progress', Icon: TrendingUp, prop: 'onGoToProgress',
    description: 'See your improvement over time',
  },
  {
    key: 'channelstats', label: 'Channel Stats', Icon: Activity, prop: 'onGoToChannelStats',
    description: 'Compare telemetry across sessions',
  },
  {
    key: 'search', label: 'Search', Icon: Search, prop: 'onGoToSearch',
    description: 'Find laps by sector or channel values',
  },
  {
    // Not a destination: a report only means something over a selection, so
    // it is reached from the Sessions page's bulk toolbar. No page passes
    // onGoToReport; the entry stays so the nav can name the page while on it.
    key: 'report', label: 'Session Report', Icon: FileText, prop: 'onGoToReport',
    description: 'Build a printable report from selected sessions',
  },
  {
    key: 'simlab', label: 'Similarity Lab', Icon: Radar, prop: 'onGoToSimLab',
    description: 'Explore driver-style similarity',
  },
  {
    key: 'issues', label: 'Issue Reports', Icon: Bug, prop: 'onGoToIssues',
    description: 'Review problems users have reported', badge: 'openIssueCount',
  },
  {
    // The one item that leaves the app: the public tools live as static pages
    // outside the SPA, so this is an href rather than an onGoTo* prop. It sits
    // last because the headings below are injected by position.
    key: 'tools', label: 'Tools', Icon: Wrench, href: '/tools/',
    description: 'Free setup calculators',
  },
];

const CURRENT_LABELS = Object.fromEntries(MENU_ITEMS.map(item => [item.key, item.label]));
const BETA_KEYS = new Set(MENU_ITEMS.filter(item => item.beta).map(item => item.key));
CURRENT_LABELS.session = 'Session Analysis';

export default function AppNav({
  current,
  onGoToStudio,
  onGoToSessions,
  onGoToLeaderboard,
  onGoToProgress,
  onGoToChannelStats,
  onGoToSearch,
  onGoToReport,
  onGoToSimLab,
  onGoToIssues,
  openIssueCount = null,
  onReportIssue,
  onOpenUserSettings,
  onLogout,
  adminModeActive,
  onToggleAdminMode,
  driverProfile,
  helpPage,
  shell = false,
  contextTools = null,
  compact = false,
  compactMeta = null,
  currentUser,
  onStartMasquerade,
}) {
  const [open, setOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [desktopDownloadOpen, setDesktopDownloadOpen] = useState(false);
  const pickerRef = useRef(null);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);
  const focusMenuOnOpen = useRef(false);
  const accountRef = useRef(null);
  const accountTriggerRef = useRef(null);
  const accountItemRefs = useRef([]);
  const focusAccountOnOpen = useRef(false);
  const menuId = useId();
  const accountMenuId = useId();

  const propMap = {
    onGoToStudio, onGoToSessions, onGoToLeaderboard, onGoToProgress,
    onGoToChannelStats, onGoToSearch, onGoToReport, onGoToSimLab, onGoToIssues,
  };
  const badges = { openIssueCount };
  const items = MENU_ITEMS.filter(({ key, prop, href }) => href || propMap[prop] || current === key);
  const currentLabel = CURRENT_LABELS[current] || 'Explore';
  const mobileLabel = current === 'session' ? 'Analysis' : currentLabel;
  const currentBeta = BETA_KEYS.has(current);
  const canStartMasquerade = !!(onStartMasquerade && currentUser?.is_admin && !currentUser?.masquerade);
  const canDownloadDesktop = !!(currentUser && onLogout);
  // Roving-focus indices for the account menu, in render order.
  let accountSlot = 0;
  const settingsIndex = onOpenUserSettings ? accountSlot++ : null;
  const reportIndex = onReportIssue ? accountSlot++ : null;
  const desktopDownloadIndex = canDownloadDesktop ? accountSlot++ : null;
  const masqueradeIndex = canStartMasquerade ? accountSlot++ : null;
  const logoutIndex = onLogout ? accountSlot++ : null;

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!pickerRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Tab') {
        setOpen(false);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!accountOpen) return undefined;

    const onPointerDown = (event) => {
      if (!accountRef.current?.contains(event.target)) setAccountOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Tab') {
        setAccountOpen(false);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setAccountOpen(false);
        accountTriggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [accountOpen]);

  useEffect(() => {
    if (open && focusMenuOnOpen.current) {
      focusMenuOnOpen.current = false;
      requestAnimationFrame(() => itemRefs.current[0]?.focus());
    }
  }, [open]);

  useEffect(() => {
    if (accountOpen && focusAccountOnOpen.current) {
      focusAccountOnOpen.current = false;
      requestAnimationFrame(() => accountItemRefs.current[0]?.focus());
    }
  }, [accountOpen]);

  const openFromKeyboard = () => {
    focusMenuOnOpen.current = true;
    setAccountOpen(false);
    setOpen(true);
  };

  const onTriggerKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      openFromKeyboard();
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (open) setOpen(false);
      else openFromKeyboard();
    }
  };

  const toggleAccountFromKeyboard = () => {
    if (accountOpen) {
      setAccountOpen(false);
      return;
    }
    focusAccountOnOpen.current = true;
    setOpen(false);
    setAccountOpen(true);
  };

  const onAccountItemKeyDown = (event, index) => {
    const count = accountItemRefs.current.filter(Boolean).length;
    let next = null;
    if (event.key === 'ArrowDown') next = (index + 1) % count;
    if (event.key === 'ArrowUp') next = (index - 1 + count) % count;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = count - 1;
    if (next == null) return;
    event.preventDefault();
    accountItemRefs.current[next]?.focus();
  };

  const onItemKeyDown = (event, index) => {
    // A menuitem answers to Space; a link does not, so activate it by hand.
    if (event.key === ' ' && event.currentTarget.tagName === 'A') {
      event.preventDefault();
      event.currentTarget.click();
      return;
    }
    let next = null;
    if (event.key === 'ArrowDown') next = (index + 1) % items.length;
    if (event.key === 'ArrowUp') next = (index - 1 + items.length) % items.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = items.length - 1;
    if (next == null) return;
    event.preventDefault();
    itemRefs.current[next]?.focus();
  };

  return (
    <>
    <nav className={`${styles.nav} ${shell ? styles.shell : ''} ${compact ? styles.compact : ''}`} aria-label="Application navigation">
      <div className={styles.primaryNav}>
        {items.length > 0 && (
          <div className={styles.panelPicker} ref={pickerRef}>
            <button
              ref={triggerRef}
              className={styles.sectionBtn}
              onClick={() => { setAccountOpen(false); setOpen(value => !value); }}
              onKeyDown={onTriggerKeyDown}
              aria-haspopup="menu"
              aria-expanded={open}
              aria-controls={menuId}
              aria-label={`Navigate from ${currentLabel}`}
              title="Open page menu"
            >
              <BarChart3 size={15} aria-hidden="true" />
              <span className={styles.desktopSectionLabel}>{currentLabel}</span>
              <span className={styles.mobileSectionLabel}>{mobileLabel}</span>
              {currentBeta && <BetaBadge className={styles.betaBadge} />}
              <ChevronDown size={13} className={styles.caret} aria-hidden="true" />
            </button>
            {open && (
              <div className={styles.dropdown} id={menuId} role="menu" aria-label="Go to">
                <div className={styles.dropdownHeading}>Your data</div>
                {items.map(({ key, label, Icon, prop, description, badge, href, beta }, index) => {
                  // An href item is a real link out of the SPA; everything else
                  // switches view through its prop. Both stay focusable menuitems,
                  // so the roving-focus arithmetic below is unaffected.
                  const ItemTag = href ? 'a' : 'button';
                  const itemProps = href
                    ? { href, target: '_blank', rel: 'noopener noreferrer', onClick: () => setOpen(false) }
                    : {
                      onClick: () => {
                        setOpen(false);
                        if (current !== key) propMap[prop]?.();
                      },
                    };
                  return (
                  <div key={key} role="presentation">
                    {index > 0 && items[index - 1]?.key === 'sessions' && (
                      <div className={styles.dropdownHeading}>Analyze</div>
                    )}
                    {key === 'tools' && (
                      <div className={styles.dropdownHeading}>Elsewhere</div>
                    )}
                    <ItemTag
                      ref={element => { itemRefs.current[index] = element; }}
                      className={current === key ? styles.itemActive : styles.item}
                      {...itemProps}
                      onKeyDown={event => onItemKeyDown(event, index)}
                      role="menuitem"
                      aria-current={current === key ? 'page' : undefined}
                    >
                      <Icon size={16} className={styles.itemIcon} aria-hidden="true" />
                      <span className={styles.itemCopy}>
                        <span className={styles.itemLabel}>
                          {label}
                          {beta && <BetaBadge className={styles.betaBadge} />}
                          {badge && badges[badge] > 0 && (
                            <span className={styles.countBadge} aria-label={`${badges[badge]} open`}>{badges[badge]}</span>
                          )}
                        </span>
                        <span className={styles.itemDescription}>{description}</span>
                      </span>
                    </ItemTag>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {contextTools && <div className={styles.contextTools}>{contextTools}</div>}

      {compactMeta && <div className={styles.compactMeta}>{compactMeta}</div>}

      <div className={styles.accountNav}>
        {onToggleAdminMode && (
          <button
            className={`${styles.adminBtn} ${adminModeActive ? styles.adminBtnActive : ''}`}
            onClick={onToggleAdminMode}
            title={adminModeActive
              ? 'Admin mode active — click to switch to normal view'
              : 'Admin mode off — click to enable admin controls'}
            aria-label={adminModeActive ? 'Turn off admin mode' : 'Turn on admin mode'}
            aria-pressed={adminModeActive}
          >
            <ShieldCheck size={15} aria-hidden="true" />
            <span>Admin{adminModeActive ? '' : ' off'}</span>
          </button>
        )}
        {helpPage && <HelpButton page={helpPage} className={styles.iconBtn} title="Help" />}
        {(onOpenUserSettings || onReportIssue || canStartMasquerade || onLogout) && (
          <div className={styles.accountPicker} ref={accountRef}>
            <button
              ref={accountTriggerRef}
              className={styles.accountBtn}
              onClick={() => { setOpen(false); setAccountOpen(value => !value); }}
              onKeyDown={event => {
                if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggleAccountFromKeyboard();
                }
              }}
              title="Account"
              aria-label={`Account: ${driverProfile?.name || 'Settings'}`}
              aria-haspopup="menu"
              aria-expanded={accountOpen}
              aria-controls={accountMenuId}
            >
              <User size={15} aria-hidden="true" />
              <span>{driverProfile?.name || 'Account'}</span>
              <ChevronDown size={13} className={styles.caret} aria-hidden="true" />
            </button>
            {accountOpen && (
              <div className={styles.accountDropdown} id={accountMenuId} role="menu" aria-label="Account">
                {onOpenUserSettings && (
                  <button
                    ref={element => { accountItemRefs.current[settingsIndex] = element; }}
                    className={styles.accountItem}
                    onClick={() => { setAccountOpen(false); onOpenUserSettings(); }}
                    onKeyDown={event => onAccountItemKeyDown(event, settingsIndex)}
                    role="menuitem"
                  >
                    <User size={16} aria-hidden="true" />
                    <span>Settings</span>
                  </button>
                )}
                {onReportIssue && (
                  <button
                    ref={element => { accountItemRefs.current[reportIndex] = element; }}
                    className={styles.accountItem}
                    onClick={() => { setAccountOpen(false); onReportIssue(); }}
                    onKeyDown={event => onAccountItemKeyDown(event, reportIndex)}
                    role="menuitem"
                  >
                    <MessageSquareWarning size={16} aria-hidden="true" />
                    <span>Report a problem</span>
                  </button>
                )}
                {canDownloadDesktop && (
                  <button
                    ref={element => { accountItemRefs.current[desktopDownloadIndex] = element; }}
                    className={styles.accountItem}
                    onClick={() => { setAccountOpen(false); setDesktopDownloadOpen(true); }}
                    onKeyDown={event => onAccountItemKeyDown(event, desktopDownloadIndex)}
                    role="menuitem"
                  >
                    <Download size={16} aria-hidden="true" />
                    <span>Windows Offline App</span>
                    <BetaBadge className={styles.betaBadge} />
                  </button>
                )}
                {canStartMasquerade && (
                  <button
                    ref={element => { accountItemRefs.current[masqueradeIndex] = element; }}
                    className={styles.accountItem}
                    onClick={() => { setAccountOpen(false); onStartMasquerade(); }}
                    onKeyDown={event => onAccountItemKeyDown(event, masqueradeIndex)}
                    role="menuitem"
                  >
                    <Eye size={16} aria-hidden="true" />
                    <span>Masquerade as user</span>
                  </button>
                )}
                {onLogout && (
                  <button
                    ref={element => { accountItemRefs.current[logoutIndex] = element; }}
                    className={styles.accountItem}
                    onClick={() => { setAccountOpen(false); onLogout(); }}
                    onKeyDown={event => onAccountItemKeyDown(event, logoutIndex)}
                    role="menuitem"
                  >
                    <Power size={16} aria-hidden="true" />
                    <span>Sign out</span>
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
    {desktopDownloadOpen && (
      <DesktopDownloadDialog onClose={() => setDesktopDownloadOpen(false)} />
    )}
    </>
  );
}
